<template>
  <a-modal
    :open="visible"
    :title="`${isReactivation ? '重新激活' : '调整时间'} — 节点 #${proxyId ?? ''}`"
    :confirm-loading="submitting"
    :ok-text="isReactivation ? '确认激活' : operation === 'reduce' ? '确认减少' : '确认续费'"
    :ok-button-props="{ danger: operation === 'reduce', disabled: !newExpire || reductionInvalid || submitting }"
    :closable="!submitting"
    :mask-closable="!submitting"
    :keyboard="!submitting"
    :cancel-button-props="{ disabled: submitting }"
    cancel-text="取消"
    @ok="onSubmit"
    @cancel="$emit('update:visible', false)"
  >
    <a-alert
      v-if="isReactivation"
      type="warning"
      show-icon
      message="该节点已过期。重新激活后，到期时间将从当前时间开始计算，并在原服务器、原端口重新创建子进程。"
      style="margin-bottom:16px"
    />
    <a-alert
      v-else-if="operation === 'reduce'"
      type="warning"
      show-icon
      :message="reductionInvalid ? '减少后的到期时间必须晚于当前时间，请减少扣除时长。' : '将扣减节点有效期，请确认减少后的到期时间。'"
      style="margin-bottom:16px"
    />
    <a-spin :spinning="submitting">
      <a-radio-group
        v-if="!isReactivation"
        v-model:value="operation"
        :disabled="submitting"
        button-style="solid"
        style="margin-bottom:16px"
      >
        <a-radio-button value="extend">增加时间</a-radio-button>
        <a-radio-button value="reduce">减少时间</a-radio-button>
      </a-radio-group>

      <a-descriptions :column="1" size="small" bordered style="margin-bottom:16px">
        <a-descriptions-item :label="isReactivation ? '原到期时间' : '当前到期'">
          {{ currentExpire ? formatDateTime(currentExpire) : '—' }}
        </a-descriptions-item>
        <a-descriptions-item :label="isReactivation ? '激活后到期' : operation === 'reduce' ? '减少后' : '续费后'">
          <span :style="{ color: reductionInvalid ? '#ff4d4f' : newExpire ? '#1677ff' : '#9ca3af' }">
            {{ newExpire ? formatDateTime(newExpire) : '—' }}
          </span>
        </a-descriptions-item>
      </a-descriptions>

      <a-radio-group v-model:value="mode" style="margin-bottom:8px">
        <a-radio value="preset">预设</a-radio>
        <a-radio value="custom">自定义</a-radio>
      </a-radio-group>

      <div v-if="mode === 'preset'">
        <a-radio-group v-if="operation === 'reduce'" v-model:value="preset">
          <a-radio value="1d">1 天</a-radio>
          <a-radio value="7d">7 天</a-radio>
          <a-radio value="30d">30 天</a-radio>
          <a-radio value="1m">1 个月</a-radio>
        </a-radio-group>
        <a-radio-group v-else v-model:value="preset">
          <a-radio value="1m">1 个月</a-radio>
          <a-radio value="3m">3 个月</a-radio>
          <a-radio value="6m">6 个月</a-radio>
          <a-radio value="12m">12 个月</a-radio>
        </a-radio-group>
      </div>

      <a-space v-else>
        <a-input-number
          v-model:value="customN"
          :min="1"
          :max="customUnit === 'm' ? 24 : 730"
          style="width:120px"
        />
        <a-select v-model:value="customUnit" style="width:90px">
          <a-select-option value="d">天</a-select-option>
          <a-select-option value="m">个月</a-select-option>
        </a-select>
        <span style="color:#9ca3af;font-size:12px">
          范围 1..{{ customUnit === 'm' ? 24 : 730 }}
        </span>
      </a-space>
    </a-spin>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { extendProxy, reduceProxyTime } from '../api/proxies';
import type { ProxyItem } from '../types';

const props = defineProps<{
  visible: boolean;
  proxies: ProxyItem[];
  proxyId: number | null;
}>();
const emit = defineEmits<{
  (e: 'update:visible', v: boolean): void;
  (e: 'success', payload: { proxy_id: number; expire_at: string; reactivated: boolean; adjustment: 'extend' | 'reduce'; runtime_sync_pending?: boolean }): void;
}>();

const current = computed<ProxyItem | null>(() => {
  if (props.proxyId == null) return null;
  return props.proxies.find((p) => p.proxy_id === props.proxyId) ?? null;
});
const currentExpire = computed(() => current.value?.expire_at ?? '');
const isReactivation = computed(() => current.value?.status === 'expired');

const operation = ref<'extend' | 'reduce'>('extend');
const mode = ref<'preset' | 'custom'>('preset');
const preset = ref<'1d' | '7d' | '30d' | '1m' | '3m' | '6m' | '12m'>('1m');
const customN = ref<number>(30);
const customUnit = ref<'d' | 'm'>('d');

const submitting = ref(false);

// Preview: "currentExpire +/- (months or days)". Mirrors the backend
// model (currentExpire.AddDate) for both extension and reduction.
// Uses getUTC* for arithmetic to avoid local-timezone DST shifts, then
// formats back as a wall-clock-ish ISO string the parent can store.
const newExpire = computed<string>(() => {
  if (!currentExpire.value) return '';
  // Keep calendar arithmetic in the expiry's original fixed offset,
  // matching Go AddDate even around midnight at a month boundary.
  const source = isReactivation.value ? new Date().toISOString() : currentExpire.value;
  const offset = source.match(/([+-]\d{2}:\d{2}|Z)$/)?.[1] || 'Z';
  const start = new Date(source.replace(/([+-]\d{2}:\d{2}|Z)$/, 'Z'));
  if (Number.isNaN(start.getTime())) return '';

  let monthsToAdd = 0;
  let daysToAdd = 0;
  if (mode.value === 'preset') {
    const m = parseInt(preset.value, 10);
    if (Number.isNaN(m)) return '';
    if (preset.value.endsWith('d')) daysToAdd = m;
    else monthsToAdd = m;
  } else {
    const n = customN.value;
    if (!Number.isInteger(n) || n < 1) return '';
    if (customUnit.value === 'm') {
      if (n > 24) return '';
      monthsToAdd = n;
    } else {
      if (n > 730) return '';
      daysToAdd = n;
    }
  }
  const direction = operation.value === 'reduce' ? -1 : 1;
  const y = start.getUTCFullYear();
  const mo = start.getUTCMonth();
  const d = start.getUTCDate();
  const h = start.getUTCHours();
  const mi = start.getUTCMinutes();
  const s = start.getUTCSeconds();
  const next = new Date(
    Date.UTC(y, mo + direction * monthsToAdd, d + direction * daysToAdd, h, mi, s),
  );
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    next.getUTCFullYear() +
    '-' + pad(next.getUTCMonth() + 1) +
    '-' + pad(next.getUTCDate()) +
    'T' + pad(next.getUTCHours()) +
    ':' + pad(next.getUTCMinutes()) +
    ':' + pad(next.getUTCSeconds()) +
    offset
  );
});

const reductionInvalid = computed(() => {
  if (operation.value !== 'reduce' || !newExpire.value) return false;
  return new Date(newExpire.value).getTime() <= Date.now();
});

const formatDateTime = (s: string) => {
  if (!s) return '—';
  return new Date(s).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false });
};

watch(
  () => props.visible,
  (v) => {
    if (v) {
      operation.value = 'extend';
      mode.value = 'preset';
      preset.value = '1m';
      customN.value = 30;
      customUnit.value = 'd';
    }
  },
);

watch(operation, (value) => {
  if (value === 'reduce') {
    preset.value = '7d';
    customN.value = 7;
  } else {
    preset.value = '1m';
    customN.value = 30;
  }
});

const onSubmit = async () => {
  if (submitting.value || !newExpire.value) return;
  const id = props.proxyId;
  if (id == null) {
    message.error('找不到节点');
    return;
  }
  if (operation.value === 'reduce' && new Date(newExpire.value).getTime() <= Date.now()) {
    message.error('减少后的到期时间必须晚于当前时间');
    return;
  }
  const duration =
    mode.value === 'preset' ? preset.value : `${customN.value}${customUnit.value}`;
  submitting.value = true;
  try {
    const adjustment = isReactivation.value ? 'extend' : operation.value;
    const r = adjustment === 'reduce'
      ? await reduceProxyTime(id, duration)
      : await extendProxy(id, duration);
    const resp = r.data as { expire_at: string; reactivated?: boolean; runtime_sync_pending?: boolean };
    emit('success', {
      proxy_id: id,
      expire_at: resp.expire_at,
      reactivated: Boolean(resp.reactivated),
      adjustment,
      runtime_sync_pending: resp.runtime_sync_pending,
    });
    emit('update:visible', false);
  } catch (e: any) {
    const errCode = e?.response?.data?.error?.code;
    const msg = e?.response?.data?.error?.message || e?.message || '调整时间失败';
    if (errCode === 'CREATING') {
      message.error('节点正在初始化，请稍后再试');
    } else if (errCode === 'PORT_IN_USE') {
      message.error('原端口已被其他节点占用，无法重新激活');
    } else if (errCode === 'REGION_OCCUPIED_ON_NODE') {
      message.error('原大区已被该服务器上的其他节点占用，无法重新激活');
    } else if (errCode === 'REACTIVATION_PENDING') {
      message.warning('有效期已保存，子进程正在等待重新创建');
    } else if (errCode === 'REDUCTION_EXCEEDS_REMAINING_TIME') {
      message.error('扣除时长超过节点剩余有效期，请减少扣除时长');
    } else if (errCode === 'RUNTIME_SYNC_PENDING') {
      message.warning('到期时间已保存，agent 重连后会自动同步');
    } else if (errCode === 'AGENT_OFFLINE') {
      message.warning('agent 离线，DB 已更新，等 agent 上线后下次重连会生效');
    } else {
      message.error(msg);
    }
  } finally {
    submitting.value = false;
  }
};
</script>
